import { useQueryFactory } from './useQueryFactory'
import { affiliatedOrganizationsService } from '@/services/affiliatedOrganizations'
import { queryKeys } from '@/lib/query-keys'
import type { AffiliatedOrganization } from '@/services/affiliatedOrganizations'
import type { AdherentStructure, StructureCategory } from '@/constants/structures'
import { STRUCTURE_CATEGORY } from '@/constants/structures'

interface UseAffiliatedOrganizationsParams {
  limit?: number
  offset?: number
  ordering?: string
  search?: string
  enabled?: boolean
}

// extracts acronym from organization name; expected format: "Full Name (ACRONYME)"
function extractAcronym(name: string): string {
  const parenthesesMatch = name.match(/\(([^)]+)\)\s*$/)
  return parenthesesMatch ? parenthesesMatch[1].trim() : name
}

// extracts full name from organization name; expected format: "Full Name (ACRONYME)"
function extractFullName(name: string): string {
  const parenthesesMatch = name.match(/^(.+)\s*\([^)]+\)\s*$/)
  return parenthesesMatch ? parenthesesMatch[1].trim() : name
}

function mapToAdherentStructure(org: AffiliatedOrganization): AdherentStructure {
  let category: StructureCategory
  if (org.category === 'Structure professionnelle') {
    category = STRUCTURE_CATEGORY.PROFESSIONAL
  } else if (org.category === 'Structure scientifique') {
    category = STRUCTURE_CATEGORY.SCIENTIFIC
  } else {
    category = STRUCTURE_CATEGORY.TRAINING
  }

  return {
    id: org.id,
    acronym: extractAcronym(org.name),
    fullName: extractFullName(org.name),
    category,
  }
}

export const useAffiliatedOrganizations = (params?: UseAffiliatedOrganizationsParams) => {
  const { enabled = true, ...queryParams } = params || {}
  const queryKey = queryKeys.affiliatedOrganizations.all(queryParams)

  const query = useQueryFactory({
    queryKey,
    queryFn: () => affiliatedOrganizationsService.getAffiliatedOrganizations(queryParams),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })

  const structures: AdherentStructure[] = query.data?.results
    ? query.data.results.map(mapToAdherentStructure)
    : []

  return {
    structures,
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
